import { formatQuery } from "./formatters";

/**
 * Vérifie si un texte contient une séquence de mots qui correspondent à la query donnée
 * Pour les requêtes multi-mots comme "GLC 20", vérifie si le texte contient "GLC" suivi d'un mot commençant par "20"
 */
function hasWordStartingWith(text: string, query: string): boolean {
    if (!text || !query) return false;

    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase().trim();

    // Si la query est un seul mot, utiliser la logique originale
    if (!queryLower.includes(" ")) {
        const words = textLower.split(/\s+/);
        return words.some((word) => word.startsWith(queryLower));
    }

    // Pour les requêtes multi-mots, vérifier la séquence
    const queryWords = queryLower.split(/\s+/);
    const textWords = textLower.split(/\s+/);

    // Chercher une séquence dans le texte qui correspond à la requête
    for (let i = 0; i <= textWords.length - queryWords.length; i++) {
        let allMatch = true;

        for (let j = 0; j < queryWords.length; j++) {
            const textWord = textWords[i + j];
            const queryWord = queryWords[j];

            // Le premier mot de la requête doit être trouvé exactement ou au début d'un mot
            if (j === 0) {
                if (
                    !textWord.includes(queryWord) &&
                    !textWord.startsWith(queryWord)
                ) {
                    allMatch = false;
                    break;
                }
            } else {
                // Les mots suivants doivent commencer par le mot de la requête
                if (!textWord.startsWith(queryWord)) {
                    allMatch = false;
                    break;
                }
            }
        }

        if (allMatch) {
            return true;
        }
    }

    return false;
}

/**
 * Trouve la position du premier mot qui commence par le query dans le texte
 * Retourne { start, end } ou null si aucun match
 * Amélioré pour gérer les requêtes multi-mots
 */
function findWordMatch(
    text: string,
    query: string,
): { start: number; end: number } | null {
    if (!text || !query) return null;

    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase().trim();

    // Si la query est un seul mot, utiliser la logique originale
    if (!queryLower.includes(" ")) {
        const words = textLower.split(/\s+/);
        let currentIndex = 0;

        for (const word of words) {
            const wordStartIndex = textLower.indexOf(word, currentIndex);
            if (word.startsWith(queryLower)) {
                return {
                    start: wordStartIndex,
                    end: wordStartIndex + queryLower.length,
                };
            }
            currentIndex = wordStartIndex + word.length;
        }
        return null;
    }

    // Pour les requêtes multi-mots, trouver la séquence complète
    const queryWords = queryLower.split(/\s+/);
    const firstQueryWord = queryWords[0];

    // Trouver le début de la première correspondance
    const firstWordIndex = textLower.indexOf(firstQueryWord);
    if (firstWordIndex === -1) return null;

    // Calculer la fin en se basant sur la longueur de la requête complète
    const queryEndIndex = firstWordIndex + queryLower.length;

    return {
        start: firstWordIndex,
        end: Math.min(queryEndIndex, textLower.length),
    };
}

/**
 * Trouve le mot le plus court qui correspond au query dans le texte
 * Retourne la longueur du mot, ou Infinity si aucun match
 * Amélioré pour gérer les requêtes multi-mots
 */
function getShortestMatchingWordLength(text: string, query: string): number {
    if (!text || !query) return Infinity;

    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase().trim();

    // Si c'est un seul mot, utiliser la logique originale
    if (!queryLower.includes(" ")) {
        const words = textLower.split(/\s+/);
        let shortestLength = Infinity;
        for (const word of words) {
            if (word.startsWith(queryLower)) {
                shortestLength = Math.min(shortestLength, word.length);
            }
        }
        return shortestLength;
    }

    // Pour les requêtes multi-mots, retourner la longueur de la requête complète si elle match
    if (hasWordStartingWith(textLower, queryLower)) {
        return queryLower.length;
    }

    return Infinity;
}

/**
 * Plugin de suggestions personnalisé pour Typesense
 */
export function createTypesenseQuerySuggestionsPlugin(
    autocompleteSearchClient: any,
    indexName: string,
    page: any,
    setInstantSearchUiState: any,
    toMarketplace: any,
) {
    return {
        getSources({ query }: { query: string }) {
            if (!query) {
                // Retourner des suggestions populaires quand il n'y a pas de query
                return [
                    {
                        sourceId: "querySuggestionsPlugin",
                        async getItems() {
                            try {
                                // Recherche de termes populaires dans l'index principal
                                const results =
                                    await autocompleteSearchClient.search([
                                        {
                                            indexName: indexName,
                                            params: {
                                                query: "*",
                                                hitsPerPage: 5,
                                                attributesToRetrieve: [
                                                    "make",
                                                    "model",
                                                    "specification",
                                                ],
                                            },
                                        },
                                    ]);

                                // Extraire des suggestions à partir des résultats
                                const suggestions: Array<{ query: string }> =
                                    [];
                                const seenQueries = new Set<string>();

                                results.results[0].hits.forEach((hit: any) => {
                                    const makeModel =
                                        `${hit.make} ${hit.model}`.trim();
                                    if (
                                        makeModel &&
                                        !seenQueries.has(makeModel) &&
                                        suggestions.length < 5
                                    ) {
                                        suggestions.push({
                                            query: makeModel,
                                        });
                                        seenQueries.add(makeModel);
                                    }
                                });

                                return suggestions;
                            } catch (error) {
                                console.warn(
                                    "Erreur lors de la récupération des suggestions:",
                                    error,
                                );
                                return [];
                            }
                        },
                        onSelect({ item, state }: { item: any; state: any }) {
                            if (page.url.startsWith("/marketplace")) {
                                setInstantSearchUiState({ query: item.query });
                            } else {
                                toMarketplace({ query: item.query });
                            }
                        },
                        templates: {
                            item({
                                item,
                                html,
                                state,
                            }: {
                                item: any;
                                html: any;
                                state: any;
                            }) {
                                const formattedQuery = formatQuery(item.query);
                                // Highlight de la partie de la recherche qui match
                                const userInput = (
                                    state.query || ""
                                ).toLowerCase();

                                if (!userInput) {
                                    return html`<span
                                        class="aa-ItemContentTitle"
                                        >${formattedQuery}</span
                                    >`;
                                }

                                const wordMatch = findWordMatch(
                                    formattedQuery,
                                    userInput,
                                );

                                if (!wordMatch) {
                                    return html`<span
                                        class="aa-ItemContentTitle"
                                        >${formattedQuery}</span
                                    >`;
                                }

                                const beforeMatch = formattedQuery.substring(
                                    0,
                                    wordMatch.start,
                                );
                                const match = formattedQuery.substring(
                                    wordMatch.start,
                                    wordMatch.end,
                                );
                                const afterMatch = formattedQuery.substring(
                                    wordMatch.end,
                                );

                                return html`<span class="aa-ItemContentTitle">
                                    ${beforeMatch}<mark>${match}</mark>${afterMatch}
                                </span>`;
                            },
                        },
                    },
                ];
            }

            // Suggestions basées sur la query de l'utilisateur
            return [
                {
                    sourceId: "querySuggestionsPlugin",
                    async getItems() {
                        try {
                            const results =
                                await autocompleteSearchClient.search([
                                    {
                                        indexName: indexName,
                                        params: {
                                            query: query,
                                            hitsPerPage: 50,
                                            attributesToRetrieve: [
                                                "suggestion_make",
                                                "suggestion_make_model",
                                                "suggestion_make_model_specification",
                                            ],
                                        },
                                    },
                                ]);

                            // Extraire des suggestions à partir des résultats
                            const suggestions: Array<{ query: string }> = [];
                            const seenQueries = new Set<string>();

                            const queryLower = query.toLowerCase();

                            // Collecter les suggestions par type
                            const makeSuggestions: Array<{ query: string }> =
                                [];
                            const makeModelSuggestions: Array<{
                                query: string;
                            }> = [];
                            const makeModelSpecificationSuggestions: Array<{
                                query: string;
                            }> = [];

                            results.results[0].hits.forEach((hit: any) => {
                                // Suggestions de marque
                                if (
                                    hit.suggestion_make &&
                                    !seenQueries.has(hit.suggestion_make) &&
                                    hasWordStartingWith(
                                        hit.suggestion_make,
                                        queryLower,
                                    )
                                ) {
                                    makeSuggestions.push({
                                        query: hit.suggestion_make,
                                    });
                                    seenQueries.add(hit.suggestion_make);
                                }

                                // Suggestions marque + modèle
                                if (
                                    hit.suggestion_make_model &&
                                    !seenQueries.has(
                                        hit.suggestion_make_model,
                                    ) &&
                                    hasWordStartingWith(
                                        hit.suggestion_make_model,
                                        queryLower,
                                    )
                                ) {
                                    makeModelSuggestions.push({
                                        query: hit.suggestion_make_model,
                                    });
                                    seenQueries.add(hit.suggestion_make_model);
                                }

                                // Suggestions marque + modèle + spécification
                                if (
                                    hit.suggestion_make_model_specification &&
                                    !seenQueries.has(
                                        hit.suggestion_make_model_specification,
                                    ) &&
                                    hasWordStartingWith(
                                        hit.suggestion_make_model_specification,
                                        queryLower,
                                    )
                                ) {
                                    makeModelSpecificationSuggestions.push({
                                        query: hit.suggestion_make_model_specification,
                                    });
                                    seenQueries.add(
                                        hit.suggestion_make_model_specification,
                                    );
                                }
                            });

                            // Fonction de tri par longueur du mot matchant
                            const sortByMatchLength = (
                                suggestions: Array<{ query: string }>,
                            ) => {
                                return suggestions.sort((a, b) => {
                                    const lengthA =
                                        getShortestMatchingWordLength(
                                            a.query,
                                            queryLower,
                                        );
                                    const lengthB =
                                        getShortestMatchingWordLength(
                                            b.query,
                                            queryLower,
                                        );

                                    // Si les longueurs sont différentes, prioriser le plus court
                                    if (lengthA !== lengthB) {
                                        return lengthA - lengthB;
                                    }

                                    // Si les longueurs sont égales, trier par ordre alphabétique
                                    return a.query.localeCompare(b.query);
                                });
                            };

                            // Trier chaque catégorie séparément
                            const sortedMakeSuggestions = sortByMatchLength([
                                ...makeSuggestions,
                            ]);
                            const sortedMakeModelSuggestions =
                                sortByMatchLength([...makeModelSuggestions]);
                            const sortedMakeModelSpecificationSuggestions =
                                sortByMatchLength([
                                    ...makeModelSpecificationSuggestions,
                                ]);

                            // Construire le tableau final de suggestions (max 7)
                            // En priorisant : make trié, puis make_model trié, puis make_model_specification trié
                            suggestions.push(...sortedMakeSuggestions);
                            suggestions.push(...sortedMakeModelSuggestions);
                            suggestions.push(
                                ...sortedMakeModelSpecificationSuggestions,
                            );

                            // Limiter à 7 suggestions maximum
                            suggestions.splice(7);

                            return suggestions;
                        } catch (error) {
                            console.warn(
                                "Erreur lors de la récupération des suggestions:",
                                error,
                            );
                            return [];
                        }
                    },
                    onSelect({ item, state }: { item: any; state: any }) {
                        if (page.url.startsWith("/marketplace")) {
                            setInstantSearchUiState({ query: item.query });
                        } else {
                            toMarketplace({ query: item.query });
                        }
                        state.query = formatQuery(item.query);
                    },
                    templates: {
                        item({
                            item,
                            html,
                            state,
                        }: {
                            item: any;
                            html: any;
                            state: any;
                        }) {
                            const formattedQuery = formatQuery(item.query);
                            const userInput = (state.query || "").toLowerCase();

                            if (!userInput) {
                                return html`<span class="aa-ItemContentTitle"
                                    >${formattedQuery}</span
                                >`;
                            }

                            const wordMatch = findWordMatch(
                                formattedQuery,
                                userInput,
                            );

                            if (!wordMatch) {
                                return html`<span class="aa-ItemContentTitle"
                                    >${formattedQuery}</span
                                >`;
                            }

                            const beforeMatch = formattedQuery.substring(
                                0,
                                wordMatch.start,
                            );
                            const match = formattedQuery.substring(
                                wordMatch.start,
                                wordMatch.end,
                            );
                            const afterMatch = formattedQuery.substring(
                                wordMatch.end,
                            );

                            return html`<span class="aa-ItemContentTitle">
                                ${beforeMatch}<mark>${match}</mark>${afterMatch}
                            </span>`;
                        },
                    },
                },
            ];
        },
    };
}
